import {
  IBinLocation,
  IGoodsSortingRequest,
  IBusinessPartnerReceiptLocation,
  IGoodsReceiptBatchLots,
  IGoodsReceiptHeader,
  IGoodsReceiptHeaderId,
  INotifyReceiptIssue,
  IAutocompleteEntry,
  IAttachment,
  IGoodsReceiptRowProgress,
  ISalesReturnHeader,
  ISalesReturnAttachment,
  IISalesReturnsDao$ICompanion$IAllowedBatchesSerials,
  ISalesReturnRowProgressScannedLine,
  IFinishedGoodsReceipt,
  IFinishedGoodsReceiptRow,
  IIGoodsReceiptDao$ICompanion$IPlannedGoodsReceiptDraft,
  IErpAttachment,
  IGoodsReceiptRow,
  IGoodsReceiptIssue,
  ICodeVisualCode,
} from 'interfaces';
import { Registry } from '.';
import moment from 'moment';

export default ({ get, post, destroy }: Registry) => {
  return {
    finishedGoodsReceipts: {
      getGoodsReceipts: (from: Date, to: Date): Promise<IFinishedGoodsReceipt[]> => get(
        `/api/v1/goods_receipt/finished_receipts`,
        { from: moment(from).format('YYYY-MM-DD'), to: moment(to).format('YYYY-MM-DD') }
      ),
      getGoodsReceiptLines: (code: string): Promise<IFinishedGoodsReceiptRow[]> => get(`/api/v1/goods_receipt/finished_receipts/${code}/rows`,),

      printDocuments: (code: string, printerId: any) => post(`/api/v1/goods_receipt/finished_receipts/${code}/printDocuments`, {}, { printerId }),

      attachments: {
        get: (code: string) => get<IErpAttachment[]>(
          `/api/v1/goods_receipt/finished_receipts/${code}/attachments`
        ),
        download: (code: string, acode: string) => get<{ file: string }>(
          `/api/v1/goods_receipt/finished_receipts/${code}/attachments/${acode}`
        ),
        add: (code: string, attachment: IAttachment) => post(
          `/api/v1/goods_receipt/finished_receipts/${code}/attachments`,
          attachment
        )
      }
    },

    goodsReceipts: {
      createPlannedGoodsReceipt: (draft: IIGoodsReceiptDao$ICompanion$IPlannedGoodsReceiptDraft): Promise<void> => post('/api/v1/goods_receipt/plannedGoodsReceipt', draft),

      getGoodsReceipts: (): Promise<Array<IGoodsReceiptHeader>> => get(`/api/v1/goods_receipt/`),
      getGoodsReceiptLines: (id: IGoodsReceiptHeaderId) => post<IGoodsReceiptRow[]>('/api/v1/goods_receipt/lines', id),
      openGoodsReceipt: (id: IGoodsReceiptHeaderId): Promise<string> => post('/api/v1/goods_receipt/open', id),
      batchesAndLotsOnReceipt: (id: IGoodsReceiptHeaderId): Promise<IGoodsReceiptBatchLots[]> =>
        get('/api/v1/goods_receipt/batchLots', id),

      submitIssue: (payload: IGoodsReceiptIssue) => post('/api/v1/goods_receipt/issue', payload),

      resync: (id: number) => post(`/api/v1/goods_receipt/${id}/resync`, {}),
      getActiveGoodsReceipt: (id: number): Promise<any> =>
        get(`/api/v1/goods_receipt/${id}`),
      saveAttachments: (id: number, attachments: IAttachment[]): Promise<any> =>
        post(`/api/v1/goods_receipt/${id}/attachments`, attachments),
      saveProgress: (id: number, lineId: number, progress: IGoodsReceiptRowProgress): Promise<IGoodsReceiptRowProgress> =>
        post(`/api/v1/goods_receipt/${id}/line/${lineId}/progress`, progress),
      finishGoodsReceipt: (id: string, locationMapping: Record<string, IBinLocation>): Promise<{ code: string }> =>
        post(`/api/v1/goods_receipt/${id}/finish`, locationMapping),
      deleteGoodsReceiptClaim: (id: string): Promise<void> =>
        post(`/api/v1/goods_receipt/${id}/delete`, null),
    },

    goodsSorting: {
      getRequests: (binCode: string): Promise<IGoodsSortingRequest[]> => get(`/api/v1/goods_sorting/lines/${binCode}`),
      getClients: (binCodes: string[], hydrate: boolean = false): Promise<IBusinessPartnerReceiptLocation[]> =>
        get(`/api/v1/goods_sorting/receiptsMapping/${binCodes.join(',')}`, { hydrate }),

      notifyIssuesWithReceipt: (issue: INotifyReceiptIssue) => post('/api/v1/goods_sorting/notifyIssues', issue),

      getNotificationTypes: (): Promise<IAutocompleteEntry[]> => get('/api/v1/goods_sorting/notificationTypes'),
      getNotificationSubjects: (): Promise<IAutocompleteEntry[]> => get('/api/v1/goods_sorting/notificationSubjects'),
    },

    salesGoodsReturns: {
      getOpenDocuments: (): Promise<ISalesReturnHeader[]> => get('/api/v1/sales/returns/'),
      startDocument: (code: string): Promise<{ id: number }> => post(`/api/v1/sales/returns/open/${code}`, {}),
      createForDelivery: (code: string): Promise<ICodeVisualCode> => post(`/api/v1/sales/returns/createRequest`, {}, { deliveryCode: code }),

      claim: {
        getClaim: (claimId: any): Promise<ISalesReturnHeader> => get(`/api/v1/sales/returns/claim/${claimId}`),

        getAllowedBatchesSerials: (claimId: any): Promise<{[k: number]: IISalesReturnsDao$ICompanion$IAllowedBatchesSerials}> => get(`/api/v1/sales/returns/claim/${claimId}/allowedBatchesSerials`),

        printDocuments: (claimId: any, printerId: any) => post(`/api/v1/sales/returns/claim/${claimId}/print`, {}, {printerId}),

        updateProgress: (claimId: any, scannedLine: ISalesReturnRowProgressScannedLine): Promise<ISalesReturnRowProgressScannedLine> => post(`/api/v1/sales/returns/claim/${claimId}/update_progress_line`, scannedLine),

        flushClaim: (claimId: any, targetLocation: IBinLocation): Promise<void> => post(`/api/v1/sales/returns/claim/${claimId}/flush`, { targetLocation }),
        deleteClaim: (claimId: any): Promise<void> => destroy(`/api/v1/sales/returns/claim/${claimId}`),

        attachments: {
          get: (claimId: any): Promise<ISalesReturnAttachment[]> => get(`/api/v1/sales/returns/claim/${claimId}/attachments`),
          save: (claimId: any, attachment: ISalesReturnAttachment): Promise<ISalesReturnAttachment> => post(`/api/v1/sales/returns/claim/${claimId}/attachments`, attachment),
          delete: (claimId: any, attachmentId: any): Promise<void> => destroy(`/api/v1/sales/returns/claim/${claimId}/attachments/${attachmentId}`),
        }
      }      
    },
  };
};
